export const SITE_URL = 'https://www.goodscriber.com'

export const pageMeta = {
  Home: {
    title: 'GoodScriber | AI Shadowing, Listening and Speaking Practice',
    description: 'Turn audio, video, YouTube links, and text into subtitles, natural speech, and sentence-by-sentence shadowing practice for IELTS, TOEFL, interviews, and daily speaking.',
    path: '/'
  },
  ZhHome: {
    title: 'GoodScriber | AI 英语跟读与听说练习',
    description: '把音频、视频、YouTube 链接或口语稿变成逐句字幕和自然语音，逐句精听、跟读录音、看发音评分，练雅思、托福、面试和日常口语。支持 AI 单句讲解、查词和单词本，内置雅思、托福、BBC 6 Minute English 等听力素材库。',
    path: '/zh'
  },
  Pricing: {
    title: 'Pricing: Free, Pro and Premium plans | GoodScriber',
    description: 'Compare GoodScriber Free, Pro and Premium plans and one-time credit packs for shadowing practice, pronunciation scoring, transcription and text-to-speech.',
    path: '/pricing'
  },
  Login: {
    title: 'Log in | GoodScriber',
    description: 'Log in to GoodScriber to continue your sentence-by-sentence shadowing practice.',
    path: '/login'
  },
  Signup: {
    title: 'Create your free account | GoodScriber',
    description: 'Create a free GoodScriber account and start shadowing English sentence by sentence with pronunciation scores.',
    path: '/signup'
  },
  ResetPassword: {
    title: 'Reset your password | GoodScriber',
    description: 'Reset the password for your GoodScriber account.',
    path: '/reset-password'
  },
  Material: {
    title: 'Listening Materials for Shadowing Practice | GoodScriber',
    description: 'Browse IELTS, TOEFL, podcasts, BBC 6 Minute English, TED-Ed, YouTube vlogs, and other listening materials for subtitle-based speaking practice.',
    path: '/material'
  },
  ZhMaterial: {
    title: '英语听力素材库：雅思、托福、BBC、YouTube 逐句精听跟读 | GoodScriber',
    description: '雅思、托福听力，BBC 六分钟英语，TED-Ed，英语播客，有声书和精选 YouTube 视频，全部配同步字幕，可以逐句回放、翻译、查词和跟读评分。',
    path: '/zh/material'
  },
  LearnIndex: {
    title: 'English Listening and Speaking Learning Guides | GoodScriber',
    description: 'Practical guides for shadowing, listening practice, IELTS speaking, podcasts, subtitles, and language learning with your own audio.',
    path: '/learn'
  },
  ZhLearnIndex: {
    title: '英语听力、口语与跟读学习指南 | GoodScriber',
    description: 'GoodScriber 中文学习指南，涵盖英语精听、逐句跟读、播客口语练习、雅思听力错题复盘、雅思口语、英语面试口语、双语字幕和短句复习等方法。每篇都给出可以直接照着练的步骤，并说明依据和适用边界。',
    path: '/zh/learn'
  },
  PrivacyPolicy: {
    title: 'Privacy Policy | GoodScriber',
    description: 'Read how GoodScriber handles account data, uploaded files, transcription requests, payment processing, and third-party services.',
    path: '/privacy'
  },
  TermsOfService: {
    title: 'Terms of Service | GoodScriber',
    description: 'Review the terms for using GoodScriber audio transcription, text-to-speech, subtitle, and shadowing practice tools.',
    path: '/terms'
  },
  RefundPolicy: {
    title: 'Refund Policy | GoodScriber',
    description: 'Learn when GoodScriber paid plan purchases are eligible for refunds and how to request billing support.',
    path: '/refund'
  }
}

// 后台新建、materialCatalog 里没有的分类：用接口返回的分类中英文名；接口数据还没加载回来时先用 id 生成
export function fallbackMaterialCategory(categoryId, category = null) {
  const name = categoryId.replace(/[-_]/g, ' ')
  return {
    id: categoryId,
    seoName: category?.titleEn || name,
    seoNameZh: category?.titleZh || name,
    collectionSeoName: '{title}',
    collectionSeoNameZh: '{title}'
  }
}

// language 是 'en' 或 'zh'：英文素材页在 /material/…，中文素材页在 /zh/material/…
export function materialPath(language, categoryId, collectionId, test) {
  return [language === 'zh' ? '/zh/material' : '/material', categoryId, collectionId, test && `test-${test}`]
    .filter(Boolean)
    .join('/')
}

// 按套题分页的分类（materialCatalog 里带 tests 配置）：从素材 id "册-套-节" 算出真题的套题号和节号。
// 个别 id 前面多一段册号、后面多一段哈希（"12-12-2-4-7907639c"），一并兼容
export function materialItemTest(category, collectionId, item) {
  if (!category.tests) return null
  const match = /^(?:\d+-)?\d+-(\d+)-(\d+)(?:-[0-9a-f]{8})?$/.exec(item.id)
  if (!match) return null
  const firstTest = category.tests.firstTestNumber[collectionId] || 1
  return { test: Number(match[1]) + firstTest - 1, section: Number(match[2]) }
}

// 地址里的 "test-6" → 6
export function parseTestParam(param) {
  const match = /^test-(\d+)$/.exec(param || '')
  return match ? Number(match[1]) : null
}

export function materialIndexMeta(language) {
  return language === 'zh' ? pageMeta.ZhMaterial : pageMeta.Material
}

export function materialCategoryMeta(category, language) {
  const path = materialPath(language, category.id)
  if (language === 'zh') {
    return {
      title: `${category.seoNameZh}素材：逐句精听与跟读练习 | GoodScriber`,
      description: `在线练${category.seoNameZh}：配同步字幕逐句回放，支持翻译、查词、AI 单句讲解和跟读发音评分。`,
      path
    }
  }

  const name = category.seoName.charAt(0).toUpperCase() + category.seoName.slice(1)
  return {
    title: `${name} materials for shadowing practice | GoodScriber`,
    description: `Practice ${category.seoName} with audio, synced subtitles, translation, and sentence-by-sentence shadowing in GoodScriber.`,
    path
  }
}

// collection 需要 { id, title, titleZh }。模板里 {title} 换成合集标题，{id} 换成合集 id
export function materialCollectionName(category, collection, language) {
  const template = language === 'zh' ? category.collectionSeoNameZh : category.collectionSeoName
  const title = language === 'zh' ? collection.titleZh || collection.title : collection.title
  return template.replace('{title}', title).replace('{id}', collection.id)
}

export function materialCollectionMeta(category, collection, language) {
  const name = materialCollectionName(category, collection, language)
  const isVideo = category.id.startsWith('youtube-')
  const path = materialPath(language, category.id, collection.id)

  if (language === 'zh') {
    return {
      title: isVideo
        ? `${name}带字幕：逐句跟读练口语 | GoodScriber`
        : `${name}音频带字幕：逐句精听跟读 | GoodScriber`,
      description: `在线${isVideo ? '看' : '听'}${name}，配同步字幕逐句回放，支持翻译、查词、AI 单句讲解和跟读发音评分。`,
      path
    }
  }

  return {
    title: isVideo
      ? `${name} with Subtitles for English Practice | GoodScriber`
      : `${name} Audio with Transcript | GoodScriber`,
    description: `${isVideo ? 'Watch' : 'Listen to'} ${name} online with ${isVideo ? 'synced subtitles' : 'a synced transcript'}. Replay sentence by sentence, check translations, get AI explanations, and shadow each line in GoodScriber.`,
    path
  }
}

export function materialTestMeta(category, collection, test, language) {
  const name = `${materialCollectionName(category, collection, language)} Test ${test}`
  const path = materialPath(language, category.id, collection.id, test)

  if (language === 'zh') {
    return {
      title: `${name} 音频带字幕：逐句精听跟读 | GoodScriber`,
      description: `在线听${name} 的每个 Section，配同步字幕逐句回放，支持翻译、查词、AI 单句讲解和跟读发音评分。`,
      path
    }
  }

  return {
    title: `${name} Audio with Transcript | GoodScriber`,
    description: `Listen to every section of ${name} online with a synced transcript. Replay sentence by sentence, check translations, get AI explanations, and shadow each line in GoodScriber.`,
    path
  }
}
